/**
 * TouchControlsHUD.ts
 * 모바일 화면 세로 규격 표준화 전용 가상 D-Pad 조이스틱 및 퀵 터치 액션 버튼 HUD
 */

import { Direction8, EntityState } from '../../core/Types';

export class TouchControlsHUD {
  private containerEl: HTMLElement | null = null;
  private onMoveCallback?: (screenDx: number, screenDy: number, dir: Direction8 | null) => void;
  private onTargetStickCallback?: () => void;
  private onTargetCycleCallback?: () => void;
  private isPointerDown = false;
  private stickCenter = { x: 0, y: 0 };
  private stickThumbEl: HTMLElement | null = null;
  private activeVector = { dx: 0, dy: 0 };

  constructor(
    onMove: (screenDx: number, screenDy: number, dir: Direction8 | null) => void,
    onTargetStick?: () => void,
    onTargetCycle?: () => void
  ) {
    this.onMoveCallback = onMove;
    this.onTargetStickCallback = onTargetStick;
    this.onTargetCycleCallback = onTargetCycle;
  }

  public mount(parent: HTMLElement): void {
    if (this.containerEl) return;

    const el = document.createElement('div');
    el.id = 'touch-controls-hud';
    el.className = 'touch-controls-hud';
    el.style.position = 'fixed';
    el.style.inset = '0';
    el.style.pointerEvents = 'none'; // 하위 특정 버튼만 터치 반응
    el.style.zIndex = '60';
    el.style.userSelect = 'none';
    el.style.display = 'none'; // CSS 미디어쿼리로 모바일(<=768px)에서만 flex 표시

    el.innerHTML = `
      <!-- 좌측 하단 가상 아날로그 조이스틱 패드 -->
      <div id="touch-joystick-base" style="
        position: absolute;
        left: 20px;
        bottom: 120px;
        width: 110px;
        height: 110px;
        border-radius: 50%;
        background: radial-gradient(circle, rgba(15, 23, 42, 0.75) 0%, rgba(30, 41, 59, 0.6) 100%);
        border: 2px solid rgba(245, 158, 11, 0.6);
        box-shadow: 0 4px 16px rgba(0,0,0,0.6), inset 0 0 14px rgba(245, 158, 11, 0.2);
        pointer-events: auto;
        touch-action: none;
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <!-- 방향 십자선 가이드 -->
        <div style="position: absolute; width: 100%; height: 1px; background: rgba(255,255,255,0.15);"></div>
        <div style="position: absolute; height: 100%; width: 1px; background: rgba(255,255,255,0.15);"></div>
        
        <!-- 이동 노브 (Thumb) -->
        <div id="touch-joystick-thumb" style="
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: linear-gradient(135deg, #f59e0b, #d97706);
          border: 2px solid #fef08a;
          box-shadow: 0 2px 8px rgba(0,0,0,0.8);
          pointer-events: none;
          transition: transform 0.05s ease-out;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
        ">🧭</div>
      </div>

      <!-- 우측 하단 퀵 모바일 타깃/따라가기 플로팅 버튼들 -->
      <div id="touch-quick-actions" style="
        position: absolute;
        right: 16px;
        bottom: 120px;
        display: flex;
        flex-direction: column;
        gap: 10px;
        pointer-events: auto;
      ">
        <button id="touch-btn-target" style="
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: linear-gradient(135deg, #0284c7, #0369a1);
          border: 1.5px solid #38bdf8;
          color: #fff;
          font-size: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 4px 14px rgba(0,0,0,0.6);
        " title="가장 가까운 대상 타겟팅 (Tab)">🎯</button>

        <button id="touch-btn-stick" style="
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: linear-gradient(135deg, #d97706, #b45309);
          border: 1.5px solid #fde047;
          color: #fff;
          font-size: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 4px 14px rgba(0,0,0,0.6);
        " title="대상 자동 따라가기 (F)">🚶</button>
      </div>
    `;

    parent.appendChild(el);
    this.containerEl = el;

    this.stickThumbEl = el.querySelector('#touch-joystick-thumb') as HTMLElement;
    const baseEl = el.querySelector('#touch-joystick-base') as HTMLElement;

    // 조이스틱 터치 이벤트 바인딩
    if (baseEl) {
      const handlePointerStart = (clientX: number, clientY: number) => {
        this.isPointerDown = true;
        const rect = baseEl.getBoundingClientRect();
        this.stickCenter = {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
        };
        this.updateJoystick(clientX, clientY);
      };

      const handlePointerMove = (clientX: number, clientY: number) => {
        if (!this.isPointerDown) return;
        this.updateJoystick(clientX, clientY);
      };

      const handlePointerEnd = () => {
        this.isPointerDown = false;
        this.activeVector = { dx: 0, dy: 0 };
        if (this.stickThumbEl) {
          this.stickThumbEl.style.transform = 'translate(0px, 0px)';
        }
        this.onMoveCallback?.(0, 0, null);
      };

      baseEl.addEventListener('touchstart', (e) => {
        e.preventDefault();
        const t = e.touches[0];
        handlePointerStart(t.clientX, t.clientY);
      }, { passive: false });

      window.addEventListener('touchmove', (e) => {
        if (!this.isPointerDown) return;
        const t = e.touches[0];
        handlePointerMove(t.clientX, t.clientY);
      }, { passive: false });

      window.addEventListener('touchend', () => {
        if (this.isPointerDown) handlePointerEnd();
      });
      window.addEventListener('touchcancel', () => {
        if (this.isPointerDown) handlePointerEnd();
      });

      // 마우스로도 테스트 가능하도록 지원
      baseEl.addEventListener('mousedown', (e) => {
        handlePointerStart(e.clientX, e.clientY);
      });
      window.addEventListener('mousemove', (e) => {
        if (this.isPointerDown) handlePointerMove(e.clientX, e.clientY);
      });
      window.addEventListener('mouseup', () => {
        if (this.isPointerDown) handlePointerEnd();
      });
    }

    // 퀵 버튼 바인딩
    el.querySelector('#touch-btn-target')?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.onTargetCycleCallback?.();
    });

    el.querySelector('#touch-btn-stick')?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.onTargetStickCallback?.();
    });
  }

  private updateJoystick(clientX: number, clientY: number): void {
    const rawDx = clientX - this.stickCenter.x;
    const rawDy = clientY - this.stickCenter.y;
    const maxRadius = 40; // 최대 이동 반경 40px

    const dist = Math.hypot(rawDx, rawDy);
    const clampedDist = Math.min(dist, maxRadius);
    const angle = Math.atan2(rawDy, rawDx);

    const normX = Math.cos(angle);
    const normY = Math.sin(angle);

    const thumbX = normX * clampedDist;
    const thumbY = normY * clampedDist;

    if (this.stickThumbEl) {
      this.stickThumbEl.style.transform = `translate(${thumbX.toFixed(1)}px, ${thumbY.toFixed(1)}px)`;
    }

    // 데드존 체크 (10px 이상 당겼을 때 이동)
    if (dist < 8) {
      this.activeVector = { dx: 0, dy: 0 };
      this.onMoveCallback?.(0, 0, null);
      return;
    }

    // 정규화된 스크린 방향 벡터 (X, Y)
    this.activeVector = { dx: normX, dy: normY };
    this.onMoveCallback?.(normX, normY, this.calcDirection(normX, normY));
  }

  private calcDirection(sx: number, sy: number): Direction8 {
    const deg = (Math.atan2(sy, sx) * 180) / Math.PI;
    if (deg >= -22.5 && deg < 22.5) return 'E';
    if (deg >= 22.5 && deg < 67.5) return 'SE';
    if (deg >= 67.5 && deg < 112.5) return 'S';
    if (deg >= 112.5 && deg < 157.5) return 'SW';
    if (deg >= 157.5 || deg < -157.5) return 'W';
    if (deg >= -157.5 && deg < -112.5) return 'NW';
    if (deg >= -112.5 && deg < -67.5) return 'N';
    return 'NE';
  }

  public getActiveVector(): { dx: number; dy: number } {
    return this.activeVector;
  }
}
